import { createLogger } from '@/lib/logger';

const log = createLogger('swUpdate');

let updateSWFn: ((reload?: boolean) => Promise<void>) | null = null;

/**
 * Register the service worker. The `injectManifest` strategy requires manual
 * registration (vite-plugin-pwa does not auto-register it). When a new build is
 * waiting, [onWaiting] fires so the UpdateGate can offer/force an update.
 */
export async function registerForUpdates(onWaiting: () => void): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const { registerSW } = await import('virtual:pwa-register');
    updateSWFn = registerSW({
      immediate: true,
      onNeedRefresh: () => onWaiting(),
      onRegisterError: (e) => log.warn('SW register error', e),
    });
  } catch (e) {
    log.warn('SW registration unavailable', e);
  }
}

/**
 * Resolve once the registration has a freshly-installed worker sitting in the
 * `waiting` state (or time out). Needed because the server version gate can ask
 * the driver to update *before* the SW has detected the new build, so we must
 * trigger and await the install ourselves rather than assume one is waiting.
 */
function waitForWaiting(
  reg: ServiceWorkerRegistration,
  timeoutMs: number,
): Promise<ServiceWorker | null> {
  if (reg.waiting) return Promise.resolve(reg.waiting);
  return new Promise((resolve) => {
    let done = false;
    const finish = (sw: ServiceWorker | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(sw);
    };
    const timer = setTimeout(() => finish(reg.waiting ?? null), timeoutMs);
    const onUpdateFound = () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && reg.waiting) finish(reg.waiting);
      });
    };
    reg.addEventListener('updatefound', onUpdateFound);
    onUpdateFound();
  });
}

/**
 * Apply a pending update: force an SW update check, wait for the new worker to
 * finish installing, activate it via SKIP_WAITING, and reload once it takes
 * control.
 *
 * The install downloads the whole new precache (~0.5 MB), which can take well
 * over 10 seconds on a slow cellular link. An earlier version waited only 6s
 * and then blind-reloaded after 8s — the reload happened under the OLD worker,
 * so the driver saw the update "try" and land back on the old version. We now
 * wait out the install (bounded), and if it genuinely is not ready we report
 * 'not-ready' instead of reloading into the old build; the download keeps
 * going in the background so a retry shortly after succeeds.
 */
export async function applySwUpdate(): Promise<'reloading' | 'not-ready'> {
  let reloaded = false;
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };

  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    reload();
    return 'reloading';
  }

  // Reload the moment the new worker takes control.
  navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true });

  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      // Pull any newer SW the server may have deployed since the last check.
      await reg.update().catch(() => undefined);
      const waiting = await waitForWaiting(reg, 60_000);
      if (waiting) {
        waiting.postMessage({ type: 'SKIP_WAITING' });
        // controllerchange performs the reload; backstop in case it never
        // fires (the new worker is active by then, so reloading is correct).
        setTimeout(reload, 10_000);
        return 'reloading';
      }
      // Still downloading after the long wait: do NOT reload (that lands on
      // the old build). Let the caller tell the driver to retry shortly.
      navigator.serviceWorker.removeEventListener('controllerchange', reload);
      return 'not-ready';
    }
    // No registration at all — let vite-plugin-pwa try its own path, then
    // reload to pick up whatever is newest.
    if (updateSWFn) {
      await updateSWFn(true).catch(() => undefined);
    }
  } catch (e) {
    log.warn('applySwUpdate failed, forcing reload', e);
  }

  reload();
  return 'reloading';
}
